import React from 'react';
import { IconButton, Box, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';
import { useNavigate } from 'react-router-dom';

const PAGE_ID = 'm2_u2_x4_w_w_b';

const images = [
  { key: 'wwb_one', src: '/assets/drawable/wwb_one.jpg', alt: 'Speicher-Wassererwärmer Schema', label: 'Schema Speicher' },
  { key: 'wwb_speicher', src: '/assets/drawable/wwb_speicher.webp', alt: 'Speicher-Wassererwärmer', label: 'Speicher' },
  { key: 'wwb_wt', src: '/assets/drawable/wwb_wt.jpg', alt: 'Plattenwärmetauscher', label: 'PWT' },
  { key: 'wwb_dle', src: '/assets/drawable/wwb_dle.jpg', alt: 'Durchlauferhitzer', label: 'DLE' },
  { key: 'wwb_wt_ws', src: '/assets/drawable/wwb_wt_ws.PNG', alt: 'Speicher-Lade-System', label: 'Speicher-Lade' },
  { key: 'wwb_ps', src: '/assets/drawable/wwb_ps.PNG', alt: 'Desinfektionsschaltung', label: 'Desinfektion' }
];

const textBlocks = [
  {
    id: 'block_intro',
    heading: 'Grundprinzip der Warmwasserbereitung',
    body:
      'Ziel: Kaltes Trinkwasser auf die gewünschte Temperatur erwärmen und für den Verbrauch bereitstellen. ' +
      'Die Wärme stammt je nach Anlage aus Heizkessel, Fernwärme, Solarthermie, Wärmepumpe u.a.'
  },
  {
    id: 'block_varianten',
    heading: 'Varianten der Warmwasserbereitung',
    body: 'Hauptsächlich drei Konzepte: 1) Speicher-Wassererwärmer, 2) Durchfluss-Wassererwärmer (PWT/DLE), 3) Speicher-Lade-Systeme.'
  },
  {
    id: 'block_speicher_funktionsweise',
    heading: '1. Speicher-Wassererwärmer – Funktionsweise',
    body:
      'Gedämmter Speicher bevorratet Trinkwasser. Ein interner Wärmetauscher (meist Rohrschlange) wird vom Heizkreis durchflossen und überträgt die Wärme auf das Trinkwasser. ' +
      'Entnahme oben, Nachströmen von Kaltwasser unten.'
  },
  {
    id: 'block_speicher_vor_nach',
    heading: 'Speicher – Vorteile und Nachteile',
    body:
      'Vorteile: Gut für Spitzenlasten (Vorrat), geringere Erzeugerleistung möglich, gut kombinierbar mit Solarthermie.\n' +
      'Nachteile: Platzbedarf, Speicherverluste, Legionellenrisiko bei falschem Betrieb, Aufheizzeit.'
  },
  {
    id: 'block_speicher_bauteile',
    heading: 'Speicher – Typische Bauteile',
    body:
      'Warmwasserspeicher (mit internem WT), Speicherladepumpe (bei externer Ladung), Temperaturfühler (oben/mitte/unten), Rückschlagventil, ' +
      'Sicherheitsventil (Trinkwasser), Anode (Korrosionsschutz).'
  },
  {
    id: 'block_pwt_funktionsweise',
    heading: '2. Durchfluss-Wassererwärmer (indirekt über PWT)',
    body:
      'Trinkwasser wird bedarfsgerecht über einen externen Plattenwärmetauscher erwärmt. Die Primärseite führt Heizungswasser mit Regelung über ein Ventil. ' +
      'Vorteile: Hygienisch (kein Speicher), kompakt, keine Speicherverluste, Leistung nur bei Bedarf. ' +
      'Nachteile: Hohe Erzeugerleistung für Spitzen nötig, potenziell Temperaturschwankungen bei kleinen Durchflüssen, Verkalkungsgefahr am PWT.'
  },
  {
    id: 'block_dle_funktionsweise',
    heading: 'Durchlauferhitzer (direkt, elektrisch/gas)',
    body: 'Direkte Erwärmung im Durchfluss. In großen Anlagen selten zentral, eher dezentral an Zapfstellen.'
  },
  {
    id: 'block_pwt_bauteile',
    heading: 'PWT-System – Typische Bauteile',
    body:
      'Plattenwärmetauscher, Primärpumpe, primärseitiges Regelventil, Temperaturfühler (WW-Austritt), optional Volumenstromsensor (TW), Rückschlagventile, ' +
      'Sicherheitsventil (Trinkwasser).'
  },
  {
    id: 'block_speicher_lade',
    heading: '3. Speicher-Lade-Systeme – Funktionsweise',
    body:
      'Ein Speicher wird über einen externen PWT und eine Speicherladepumpe geladen. Trinkwasser zirkuliert zwischen Speicher (unten) und PWT, ' +
      'erwärmtes Wasser wird oben eingelagert. Vorteile: Kombination aus Pufferung und effizienter Übertragung; PWT leicht zu reinigen. Nachteile: Mehr Komponenten, komplexere Regelung.'
  },
  {
    id: 'block_regel_allgemein',
    heading: 'Regelung – Überblick',
    body: 'Regelung sichert Effizienz, Komfort und Hygiene. Je nach System unterscheiden sich Zielgrößen und Stellglieder.'
  },
  {
    id: 'block_regel_speicher',
    heading: 'Regelung der Speicherladung',
    body:
      'Ziel: Speicher-Solltemperatur (z.B. 60°C) halten.\n' +
      'Logik: Fühler (oben) misst Ist. Unterer Schaltpunkt → Ladefreigabe: Speicherladepumpe EIN, ggf. Mischerstellung für Heißwasser zum WT, Erzeuger anfordern.\n' +
      'Bei Erreichen der Solltemperatur: Ladung beenden (Pumpe AUS, Mischer zurück). Oft Vorrangschaltung gegenüber Raumheizung.'
  },
  {
    id: 'block_regel_pwt',
    heading: 'Regelung PWT-Durchfluss-System',
    body:
      'Ziel: Konstante WW-Temperatur am PWT-Austritt.\n' +
      'Logik: Temperaturfühler am Austritt; primärseitiges Proportionalventil moduliert den Heizungswasserdurchfluss. Primärpumpe EIN bei Bedarf. ' +
      'Optional: Volumenstromsensor (Feed-forward) zur schnelleren Stabilisierung bei Zapfänderungen.'
  },
  {
    id: 'block_zirkulation',
    heading: 'Zirkulationspumpe – Komfort & Hygiene',
    body:
      'Problem: Abkühlung in langen Netzen, Legionellenrisiko bei Stillstand.\n' +
      'Lösung: Zirkulationsleitung zurück zum Speicher, Zirkulationspumpe kontinuierlich/zeit-/temperatur- oder bedarfsgesteuert. ' +
      'Regelung: Zeitprogramme, Temperaturfühler in Zirkulation (Schaltschwelle), ggf. bedarfsweise Taster.'
  },
  {
    id: 'block_desinfektion',
    heading: 'Thermische Desinfektion',
    body:
      'Ziel: Abtötung von Legionellen.\n' +
      'Ablauf: Periodisch (z.B. wöchentlich) gesamtes System auf ≥70°C aufheizen (Speicher + Netz + Zirkulation) und Haltezeit an jeder Zapfstelle sicherstellen. ' +
      'Sicherheitsaspekte: Verbrühungsschutz (Hinweise, ggf. temporäre Sperrung). Dokumentation nach DVGW W 551.'
  },
  {
    id: 'block_wichtige_aspekte',
    heading: 'Wichtige Aspekte für Sporthallen/Schulen',
    body:
      'Hoher, diskontinuierlicher Bedarf (viele Duschen gleichzeitig). Lange Stillstandszeiten (Ferien) erhöhen Hygieneanforderungen. ' +
      'Ausgedehnte Rohrnetze → gute Zirkulation nötig. Strenge Vorgaben (TrinkwV, DVGW).' 
  },
  {
    id: 'block_auswahl',
    heading: 'Auswahl des passenden Systems',
    body:
      'Kriterien: Spitzenbedarf/Gleichzeitigkeit, verfügbare Energie/Leistung, Platz, Kosten, Hygienevorgaben. ' +
      'Oft geeignet: Speicher-Lade-Systeme oder größere Speicher-Wassererwärmer mit sauber geplanter Zirkulation und Desinfektion.'
  }
];

const M2_U2_X4_W_W_B: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM2, canUploadImagesM2 } = useAuth();
  const canEditTexts = canEditTextsM2;
  const canUploadImages = canUploadImagesM2;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const blocks = React.useMemo(() => {
    return textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] ?? tb.body
    }));
  }, [override]);

  const fullText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.body}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const imgSrc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const actions = React.createElement(
    Box,
    { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
    React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Vorlesen' },
      React.createElement(SpeakerIcon, { sx: { fontSize: 28 } })
    ),
    React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m2_u2_x3_verbraucher') }, 'Zurück'),
    React.createElement(Button, { variant: 'contained', onClick: () => navigate('/units/m2_u2_x99_quiz') }, 'Weiter')
  );

  return React.createElement(UnitPageShell, { title: '2.2.4 Warmwasserbereitung', actions },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      // Linke Spalte: Bilder (zoombar + Admin-Upload)
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      // Rechte Spalte: Textblöcke (Admin editierbar)
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        blocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m2_u2_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M2_U2_X4_W_W_B;

